import { DataState, FetchDataPayload } from '../types/dashboard';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { startLoading, stopLoading } from '../slices/loading.slice';

import { RootState } from '../store';
import { map } from 'lodash';
import { postApi, getApi } from '../services/apis';
import { asyncInittalState, handleAsync } from './sharedSliceUtils';

const initialState: DataState = {
  data: undefined,
  totalResult: 0,
  total: 0,
  collector: null,
  ...asyncInittalState,
  activeModalData: {}
};

export type NoteType = {
  arDashboardId: number;
  message: string;
};

export const fetchData = createAsyncThunk(
  'data/fetchData',
  async (payload: FetchDataPayload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      const response = await postApi('/api/ArDashboard', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchCollector = createAsyncThunk('collector/fetchCollector', async () => {
  const response = await getApi('/api/ArDashboard/GetDefaultFilter');
  const collector = response.data;
  if (collector) {
    sessionStorage.setItem('collectorFilter', JSON.stringify(collector));
  }
  return response.data.collector;
});

export const downloadEmail = createAsyncThunk(
  'email/download',
  async (id: string, { rejectWithValue }): Promise<void> => {
    try {
      const response = await getApi(`/api/ArDashboard/DownloadEmail/${id}`, {
        responseType: 'blob'
      });

      if (response.headers['content-type'] === 'application/vnd.ms-outlook') {
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'downloadedmail.msg';

        if (contentDisposition && contentDisposition.includes('filename=')) {
          const fileNameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (fileNameMatch && fileNameMatch.length > 1) {
            fileName = fileNameMatch[1];
          }
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Unexpected content type:", response.headers['content-type']);
      }
    } catch (error: any) {
      console.error(rejectWithValue(error.message));
    }
  }
);

export const note = createAsyncThunk(
  'post/note',
  async (payload: NoteType) => {
    const response = await postApi('/api/Note', payload);
    return response.data;
  }
);

const dataSlice = createSlice({
  name: 'invoiceData',
  initialState,
  reducers: {
    setActiveModal: (state, action) => {
      state.activeModalData = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchData.pending, handleAsync.pending)
      .addCase(fetchData.fulfilled, (state, action: PayloadAction<{ totalResult: number, results: any[], filterType: string }>) => {
        state.loading = false;
        state.data = action.payload.results;
        state.totalResult = action.payload.totalResult;
      })
      .addCase(fetchData.rejected, handleAsync.rejected)
      .addCase(fetchCollector.pending, handleAsync.pending)
      .addCase(fetchCollector.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.collector = action.payload;
      })
      .addCase(note.fulfilled, (state, action) => {
        const notes = action.payload || [];
        const { arDashboardId } = action.meta.arg;
        const { data } = state;
        state.data = map(data, (item) => {
          if (item.id === arDashboardId) {
            item.notes = notes;
            state.activeModalData = item;
          }
          return item;
        });
      })
      .addCase(fetchCollector.rejected, handleAsync.rejected)
      .addCase(downloadEmail.pending, handleAsync.pending)
      .addCase(downloadEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadEmail.rejected, handleAsync.rejected);
  },
});

export const { setActiveModal } = dataSlice.actions;
export const selectedModalDetails = (state: RootState) => state.invoiceData.activeModalData || [];
export default dataSlice.reducer;



